import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { Range } from '#~/control/movement/index.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import * as Collapsible from '@studio/design-system/collapsible';
import * as Slider from '@studio/design-system/slider';
import * as ToggleGroup from '@studio/design-system/toggle/group';
import { useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import { isNil, toBoolean } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import type { RateProps } from './types.js';

/** `<Rate>` component. */
export const Rate = ({ disabled, facet, rate, ...props }: RateProps): ReactElement => {
  const { value } = rate.value;

  const { track } = useAnalytics();

  const handleValueChange = useLatestFn((nextValue: readonly number[]): void => {
    // Sanity check
    if (!nextValue.length || isNil(nextValue[0])) {
      return;
    }

    track(EVENT_NAMES.EDIT_SPEED_SETTINGS, EVENT_CATEGORIES.CONTROL_TRAY, {
      value: nextValue[0]
    });

    rate({ ...rate.value, value: nextValue[0] });
  });

  const wiring = Slider.useWiring(
    useMemo(() => [value], [value]),
    useLazyRef(() => ({
      disabled: toBoolean(disabled),
      input: {
        formatValue: (v: readonly number[]) => `${Slider.format(v)}%`,
        range: { max: 100, min: 1 }
      },
      onChange: handleValueChange,
      range: Range,
      step: 0.01
    })).current
  );

  return (
    <ToggleGroup.Container {...props}>
      <Collapsible.Root open>
        <Collapsible.Content>
          {/*
           * Per rule: playwright-stag-selectors-only (ai.knowledge/platform) —
           * STag on the rate input lets HIL e2e tests target the input
           * (single source of truth for the displayed % value) without
           * resorting to `locator('input')`. DataSTag on the thumb gives
           * tests a non-role/non-aria handle for aria-valuenow assertions.
           */}
          <Slider.Layout className='m-2 pt-4'>
            <Slider.Root {...wiring.sliderProps}>
              <Slider.Track />
              <Slider.Thumb {...DataSTag(Sel.MainRoomRightTrayControls, 'rate-thumb')} />
            </Slider.Root>
            <Slider.Input
              {...wiring.inputProps}
              {...STagNew(Sel.MainRoomRightTrayControls, 'rate-input')}
            />
          </Slider.Layout>
        </Collapsible.Content>
      </Collapsible.Root>
    </ToggleGroup.Container>
  );
};
